import {
  DownloadOutlined,
  SettingOutlined,
  CopyOutlined,
  LinkOutlined,
  CheckCircleFilled,
  FilePdfOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useAppSelector } from "@/store/hooks";
import {
  Modal,
  message,
  Input,
  Select,
  InputNumber,
  Collapse,
  Button,
} from "antd";
import { useState, useEffect } from "react";
import {
  generatePdf,
  type GeneratePdfRequestBody,
  type GeneratePdfResponse,
} from "@/api/weave-x";
import "./export-pdf-modal.scss";
import { utf8ToBase64 } from "../../utils";

interface ExportPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ExportPdfModal({ isOpen, onClose }: ExportPdfModalProps) {
  const { visualization } = useAppSelector((state) => state.canvas);

  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfResponse, setPdfResponse] = useState<GeneratePdfResponse | null>(
    null,
  );
  const [filename, setFilename] = useState("");
  const [pageSize, setPageSize] = useState<
    "single-page" | "A4" | "A3" | "us-letter" | "legal" | "tabloid"
  >("single-page");
  const [unit, setUnit] = useState<"mm" | "inch">("mm");
  const [marginTop, setMarginTop] = useState<number>(0);
  const [marginRight, setMarginRight] = useState<number>(0);
  const [marginBottom, setMarginBottom] = useState<number>(0);
  const [marginLeft, setMarginLeft] = useState<number>(0);

  useEffect(() => {
    setPdfResponse(null);
  }, [visualization.sourceCode]);

  const handleExport = async () => {
    if (!visualization.sourceCode) {
      message.error("No visualization available to export");
      return;
    }

    try {
      setIsGenerating(true);

      const base64Html = utf8ToBase64(visualization.sourceCode);

      const requestBody: GeneratePdfRequestBody = {
        htmlSourceCodeBase64: base64Html,
        pageSize,
        unit,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        filename: filename.trim() || undefined,
      };

      const response = await generatePdf(requestBody);

      if (response.downloadablePdfUrl) {
        setPdfResponse(response);
        message.success(`PDF generated successfully`);
      } else {
        message.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (pdfResponse?.downloadablePdfUrl) {
      try {
        await navigator.clipboard.writeText(pdfResponse.downloadablePdfUrl);
        message.success("Link copied to clipboard");
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        message.error("Failed to copy link");
      }
    }
  };

  const handleOpenInNewWindow = () => {
    if (pdfResponse?.downloadablePdfUrl) {
      window.open(pdfResponse.downloadablePdfUrl, "_blank");
    }
  };

  const handleDownload = async () => {
    if (pdfResponse?.downloadablePdfUrl) {
      try {
        const response = await fetch(pdfResponse.downloadablePdfUrl);

        if (!response.ok) {
          if (response.status === 404) {
            message.error("PDF not found. The file may have been deleted.");
          } else if (response.status === 403 || response.status === 410) {
            message.error("PDF link has expired. Please generate a new PDF.");
          } else {
            message.error(
              `Failed to download PDF: ${response.status} ${response.statusText}`,
            );
          }
          return;
        }

        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = pdfResponse.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error downloading PDF:", error);
        message.error("Failed to download PDF");
      }
    }
  };

  const getModalFooter = () => {
    if (pdfResponse) {
      return [
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ];
    }
    return undefined;
  };

  return (
    <Modal
      title={
        <div className="export-pdf-modal-title">
          <DownloadOutlined />
          <span>Export to PDF</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      onOk={pdfResponse ? undefined : handleExport}
      okText="Generate PDF"
      cancelText="Cancel"
      confirmLoading={isGenerating}
      footer={getModalFooter()}
      width={600}
    >
      <div className="export-pdf-content">
        {pdfResponse ? (
          <div className="pdf-success-view">
            <div className="success-icon-wrapper">
              <CheckCircleFilled className="success-icon" />
            </div>
            <div className="success-message">
              <h3>Ready for Download!</h3>
              <p>Your PDF has been generated successfully.</p>
            </div>

            <div className="download-link-section">
              <label className="link-label">Shareable Link</label>
              <p>Note: this link will expire after a period of time.</p>
              <Input
                value={pdfResponse.downloadablePdfUrl}
                readOnly
                className="share-link-input"
                addonAfter={
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={handleCopyLink}
                  >
                    Copy
                  </Button>
                }
              />
            </div>

            <div className="pdf-metadata-card">
              <div className="metadata-item">
                <FileTextOutlined className="meta-icon" />
                <div className="meta-content">
                  <span className="meta-label">Filename</span>
                  <span className="meta-value" title={pdfResponse.filename}>
                    {pdfResponse.filename ?? "Not specified"}
                  </span>
                </div>
              </div>
              <div className="metadata-item">
                <FilePdfOutlined className="meta-icon" />
                <div className="meta-content">
                  <span className="meta-label">Size</span>
                  <span className="meta-value">
                    {pdfResponse.metadata.fileSizeHuman}
                  </span>
                </div>
              </div>
              <div className="metadata-item">
                <ClockCircleOutlined className="meta-icon" />
                <div className="meta-content">
                  <span className="meta-label">Expires</span>
                  <span className="meta-value">
                    {new Date(
                      pdfResponse.metadata.presignedUrlExpiryTime,
                    ).toLocaleString([], {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <Button
                onClick={handleDownload}
                icon={<DownloadOutlined />}
                type="primary"
                size="large"
                block
                className="download-btn"
              >
                Download PDF
              </Button>
              <Button
                onClick={handleOpenInNewWindow}
                icon={<LinkOutlined />}
                size="large"
                block
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        ) : (
          <div className="pdf-form-view">
            <div className="filename-section">
              <label className="field-label">Filename (optional)</label>
              <Input
                placeholder="Leave empty for auto-generated filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                prefix={<DownloadOutlined />}
              />
              <div className="field-hint">
                The .pdf extension will be added automatically. This name is
                only used when the file is downloaded.
              </div>
            </div>

            <Collapse
              items={[
                {
                  key: "advanced",
                  label: (
                    <span className="settings-label">
                      <SettingOutlined style={{ marginRight: 8 }} />
                      Advanced Settings
                    </span>
                  ),
                  children: (
                    <div className="settings-content">
                      <div className="setting-group">
                        <label className="setting-label">Page Size</label>
                        <Select
                          value={pageSize}
                          onChange={setPageSize}
                          options={[
                            { label: "Single Page", value: "single-page" },
                            { label: "A4", value: "A4" },
                            { label: "A3", value: "A3" },
                            { label: "US Letter", value: "us-letter" },
                            { label: "Legal", value: "legal" },
                            { label: "Tabloid", value: "tabloid" },
                          ]}
                        />
                      </div>

                      <div className="setting-group">
                        <label className="setting-label">Unit</label>
                        <Select
                          value={unit}
                          onChange={setUnit}
                          options={[
                            { label: "Millimeters (mm)", value: "mm" },
                            { label: "Inches", value: "inch" },
                          ]}
                        />
                      </div>

                      <div className="setting-group">
                        <label className="setting-label">
                          Margins ({unit})
                        </label>
                        <div className="margins-grid">
                          <div className="margin-input">
                            <label className="margin-label">Top</label>
                            <InputNumber
                              value={marginTop}
                              onChange={(val) => setMarginTop(val || 0)}
                              min={0}
                            />
                          </div>
                          <div className="margin-input">
                            <label className="margin-label">Right</label>
                            <InputNumber
                              value={marginRight}
                              onChange={(val) => setMarginRight(val || 0)}
                              min={0}
                            />
                          </div>
                          <div className="margin-input">
                            <label className="margin-label">Bottom</label>
                            <InputNumber
                              value={marginBottom}
                              onChange={(val) => setMarginBottom(val || 0)}
                              min={0}
                            />
                          </div>
                          <div className="margin-input">
                            <label className="margin-label">Left</label>
                            <InputNumber
                              value={marginLeft}
                              onChange={(val) => setMarginLeft(val || 0)}
                              min={0}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
